// Composite-on-change orchestrator for the `big3-signature-events`
// Block (P-037.24 / D-132). Renders as an invisible side-effect
// component inside `<Form>` whenever `blockId === 'big3-signature-
// events'`. Watches each `big3Events[i].imageUrl` + `big3LogoUrl`
// pair; when either changes (and at least one is set), runs the
// Canvas-composite pipeline, uploads the resulting PNG to Firebase
// Storage at `uploads/big3-signature-events/composites/...`, and
// writes the resulting URL back into the same item's
// `compositeImageUrl` field via the Form's `onChange` chain.
//
// **Why a side-effect tree rather than a hook inside the field
// renderer.** The form generator walks the Zod schema purely; no per-
// Block hook surface exists. A separate side-effect component, looked
// up via `BLOCK_FORM_EFFECTS[blockId]`, keeps Form generic and lets
// the Block own its own form-side behavior.
//
// **Race protection.** A per-item `fetchTokenRef` (precedent: D-122
// §5 library browser; D-121 resize pipeline) sequences concurrent
// compose+upload jobs. Rapid edits (staff swaps logo, then image
// within 100 ms) start a second job; the first's resolution is
// discarded when its captured token != current.
//
// **Staleness-safe dispatch.** Async compose+upload completes after
// the form may have re-rendered with unrelated changes (e.g., staff
// edited the title mid-upload). `latestRef` tracks the current
// `value` + `onChange` so the dispatch always merges into the latest
// state, never overwrites unrelated fields.
//
// **Skip both-empty.** When neither `imageUrl` nor `big3LogoUrl` is
// set, the orchestrator does NOT generate a both-fallbacks composite
// (wasteful upload for a blank card). Instead the Component renders
// its fallback arm (regular EventCard shape with `/fallback-image.png`,
// no logo). On clear (sources transition set → unset), the
// orchestrator clears any stale `compositeImageUrl` so the Component
// falls back consistently.
//
// **Lazy firebase chunk.** The orchestrator dynamic-imports
// `firebase-storage` inside its async body, keeping the SDK out of the
// initial bundle. `image-composite.ts` is small + pure (no SDK), so it
// imports eagerly.
//
// **Surfaced errors.** On compose- or upload-failure, the orchestrator
// emits an error toast with a Retry action (precedent: D-129 Clear →
// Undo toast pattern via `Toast.action`). Retry re-triggers the
// effect by bumping the fetchToken; staff can also re-edit either
// source to drive a recompose.

import { useEffect, useRef, useState } from 'preact/hooks'
import type { ComponentChild } from 'preact'

import { compositeBig3Image, isCompositeSupported } from '../../../lib/image-composite'
import { addToast } from '../../toast/toast-store'
import { errorMessage } from '../../../lib/error'
import { FALLBACK_IMAGE_RAW_SRC, FALLBACK_LOGO_SRC } from '../../../components/helpers'
import { createLazyComponentLoader } from '../fields/lazy-component'

const BLOCK_ID = 'big3-signature-events'

// Lazy firebase-storage module loader. Multiple ItemEffect chains
// share one in-flight dynamic-import resolution; subsequent calls
// short-circuit via the cached Promise. Avoids a happy-dom-only
// flakiness where parallel `await import(...)` calls on the same path
// don't all resolve. Also keeps the firebase SDK out of the initial
// bundle (lazy via dynamic import). Reuses the project's existing
// `createLazyComponentLoader` factory (P-FS-05 / D-122) — third
// production consumer.
const firebaseStorageLoader = createLazyComponentLoader(
  () => import('../../../lib/firebase-storage'),
  (m) => m,
)
export const resetFirebaseStorageCacheForTest = firebaseStorageLoader.resetForTest

interface Big3Item {
  readonly title?: string
  readonly imageUrl?: string
  readonly big3LogoUrl?: string
  readonly compositeImageUrl?: string
  readonly [key: string]: unknown
}

interface BlockInputs {
  readonly big3Events?: readonly Big3Item[]
  readonly [key: string]: unknown
}

interface OrchestratorProps {
  readonly value: unknown
  readonly onChange: (next: unknown) => void
}

export function Big3CompositeOrchestrator({ value, onChange }: OrchestratorProps): ComponentChild {
  // Stable ref to the latest value + onChange. Assigned DURING render
  // (not in an effect) so any descendant effect that fires immediately
  // afterwards sees the freshest snapshot — protects against the user
  // editing unrelated fields mid-upload, AND avoids the
  // parent-effects-fire-after-child-effects ordering issue.
  const latestRef = useRef<{ value: unknown; onChange: (next: unknown) => void }>({
    value,
    onChange,
  })
  latestRef.current = { value, onChange }

  const inputs = value as BlockInputs | undefined
  const items: readonly Big3Item[] = inputs?.big3Events ?? []

  // Render one invisible Big3ItemEffect per array index. Keyed by
  // index — items lack stable IDs in V1, so reorders re-key naturally
  // (acceptable: staff doesn't reorder mid-edit at chapter scale).
  return (
    <>
      {items.map((item, idx) => (
        <Big3ItemEffect key={idx} idx={idx} item={item} latestRef={latestRef} />
      ))}
    </>
  )
}

interface ItemEffectProps {
  readonly idx: number
  readonly item: Big3Item
  readonly latestRef: { current: { value: unknown; onChange: (next: unknown) => void } }
}

function dispatchItemUpdate(
  latestRef: ItemEffectProps['latestRef'],
  idx: number,
  updater: (item: Big3Item) => Big3Item,
): void {
  const current = latestRef.current.value as BlockInputs | undefined
  const currentItems = current?.big3Events ?? []
  const target = currentItems[idx]
  if (target === undefined) return
  const updated = currentItems.map((it, i) => (i === idx ? updater(it) : it))
  latestRef.current.onChange({ ...(current ?? {}), big3Events: updated })
}

function Big3ItemEffect({ idx, item, latestRef }: ItemEffectProps): null {
  const eventSrc = normalizeSource(item.imageUrl)
  const logoSrc = normalizeSource(item.big3LogoUrl)
  // Retry nonce — Retry button bumps it to re-trigger the effect.
  // Folded into `jobKey` so a "same sources, retry pending" run is a
  // DIFFERENT job from "same sources, already succeeded". After
  // retry completes, lastJobRef stores the new jobKey so subsequent
  // renders with the same nonce + same sources skip.
  const [retryNonce, setRetryNonce] = useState(0)
  const sourceKey = `${eventSrc ?? ''}|${logoSrc ?? ''}`
  const jobKey = `${sourceKey}|retry:${retryNonce}`
  const compositeImageUrl = item.compositeImageUrl
  // Token sequence — bumped on every kick-off + on unmount. Async
  // resolutions check token vs current to drop stale work.
  const fetchTokenRef = useRef(0)
  // The jobKey for which the LAST compose successfully wrote a
  // compositeImageUrl. `null` = no compose has run yet OR a JSON-loaded
  // state's pre-existing composite. The mount logic records the
  // initial jobKey when a persisted composite is present so we don't
  // re-upload on every JSON load.
  const lastJobRef = useRef<string | null>(null)

  useEffect(() => {
    const haveSource = eventSrc !== undefined || logoSrc !== undefined

    // Clear-stale arm: sources fully cleared but a stale composite
    // persists → strip it so the Component falls back to the
    // no-composite shape.
    if (!haveSource) {
      if (compositeImageUrl !== undefined) {
        dispatchItemUpdate(latestRef, idx, (it) => {
          const next = { ...it }
          delete next.compositeImageUrl
          return next
        })
      }
      lastJobRef.current = jobKey
      return
    }

    // Skip when this exact (sources × retryNonce) job already
    // completed.
    if (lastJobRef.current === jobKey) return

    // Initial mount with pre-existing composite (JSON-loaded state):
    // record the jobKey + trust the persisted composite. No upload.
    if (lastJobRef.current === null && compositeImageUrl !== undefined) {
      lastJobRef.current = jobKey
      return
    }

    // Browser-support guard. If OffscreenCanvas / convertToBlob aren't
    // available, the Component's fallback arm renders without a
    // composite. Silent fallback — same discipline as D-121 resize.
    if (!isCompositeSupported()) {
      lastJobRef.current = jobKey
      return
    }

    const token = ++fetchTokenRef.current

    void (async () => {
      try {
        const result = await compositeBig3Image({
          eventImageUrl: eventSrc,
          big3LogoUrl: logoSrc,
          // Post-Phase-Close §item G — composite gets the RAW (un-baked)
          // fallback (see big-hero-orchestrator for the rationale).
          fallbackEventImageUrl: FALLBACK_IMAGE_RAW_SRC,
          fallbackLogoUrl: FALLBACK_LOGO_SRC,
        })
        if (token !== fetchTokenRef.current) return
        const { uploadCompositeImage } = await firebaseStorageLoader.load()
        if (token !== fetchTokenRef.current) return
        const upload = await uploadCompositeImage(result.blob, BLOCK_ID)
        if (token !== fetchTokenRef.current) return
        dispatchItemUpdate(latestRef, idx, (it) => ({ ...it, compositeImageUrl: upload.url }))
        lastJobRef.current = jobKey
      } catch (e) {
        if (token !== fetchTokenRef.current) return
        addToast({
          kind: 'error',
          message: errorMessage(e, 'Could not generate the Big 3 composite.'),
          action: {
            label: 'Retry',
            // Bump retryNonce → effect re-fires with a fresh token
            // capture. lastJobRef stays at the prior (failed) value,
            // so the "skip when last matches" guard above is gated by
            // `retryNonce === 0` (which is false right after this fires).
            onClick: () => setRetryNonce((n) => n + 1),
          },
        })
      }
    })()

    return () => {
      // Unmount or deps-change cleanup — invalidate the in-flight job.
      fetchTokenRef.current += 1
    }
  }, [jobKey, compositeImageUrl, idx, latestRef])

  return null
}

/** Treat empty string + whitespace-only same as undefined.
 *  `<ImageUrlField>`'s Clear button dispatches `onChange('')` so the
 *  persisted state has `imageUrl: ''` rather than `imageUrl: undefined`.
 *  The orchestrator normalizes so both shapes drive the same
 *  no-source semantics. */
function normalizeSource(url: string | undefined): string | undefined {
  if (url === undefined) return undefined
  const trimmed = url.trim()
  return trimmed === '' ? undefined : trimmed
}
